import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import {
  ConfigValue,
  injectCSSVariablesIntoDOM,
  FontTypes,
  MultiWheel,
} from '@longnecktech/splash-commons-fe';
import { SessionService } from '@services/session.service';
import { tap, Observable, map, BehaviorSubject, finalize } from 'rxjs';
import { SpinService } from './spin.service';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private spinService = inject(SpinService);

  private _isLoading = new BehaviorSubject<boolean>(false);

  readonly isLoading$ = this._isLoading.asObservable();

  fetchGame(): Observable<MultiWheel> {
    this._isLoading.next(true);

    return this.http
      .get<MultiWheel>(
        `${environment.backendUrl}/api/multi-wheel/game?${this.getQueryParams()}`,
      )
      .pipe(
        tap((data: MultiWheel) => {
          this.removeGlobalLoader();
          this.session.setCurrentGame(data);
          this.session.updateAuthToken();
          this.setCssVariables(data);
          this.spinService.getHistory().subscribe();
        }),
        finalize(() => {
          this._isLoading.next(false);
        }),
      );
  }

  private setCssVariables(game: MultiWheel): void {
    const styleEl = document.createElement('style');
    styleEl.textContent = injectCSSVariablesIntoDOM(game.theme, {
      defaultSecondaryCtaButtonColors: {
        '--z-cta-buttons-outline-background-color': 1,
        '--color-accent-1': 2,
      },
      defaultFonts: {
        [FontTypes.Regular]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
        [FontTypes.Bold]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Bold.ttf',
        [FontTypes.Medium]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Medium.ttf',
        [FontTypes.Headlines]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
      },
      defaultCtaBorderRadius: 28,
      customMaterialVariables: this.getOverwrittenAngularMaterialsVariables(
        game.theme.cssVariables,
      ),
    });
    // TODO: there might be bug here, that certain variable is injected without ";" at the end
    // causing all that follow it to be invalid
    // check mockResponse file
    // might be due to mocked response, double check
    document.head.appendChild(styleEl);
  }

  private getOverwrittenAngularMaterialsVariables(
    themeVariables: ConfigValue[],
  ): string {
    const baseColor = themeVariables.find((v) => v.key === '--color-base-1');
    const base25Color = themeVariables.find((v) => v.key === '--color-base-2');
    const textColor = themeVariables.find(
      (v) => v.key === '--color-neutral-100',
    );

    return `
    --mat-expansion-container-background-color: ${baseColor?.value};
    --mat-expansion-container-text-color: ${textColor?.value};
    --mat-dialog-container-shape: 0;
    --mat-dialog-container-color: ${base25Color?.value};
    `;
  }

  private getQueryParams(): string {
    const params: { instance?: string; gameUuid?: string } = {};
    if (this.session.instance) params['instance'] = this.session.instance;
    if (this.session.gameUuid) params['gameUuid'] = this.session.gameUuid;
    return new URLSearchParams(params).toString();
  }

  private removeGlobalLoader(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('global-loader');
    if (el) {
      el.remove();
    }
  }
}
